import { useMemo } from 'react';
import type { UserDocType } from '@ast-data/shared';
import { useUserDirectory } from './userDirectory';

/** The name resolver, plus whether it can actually answer yet. */
export interface UserLabelState {
    label: (user: UserDocType) => string;
    /**
     * Whether the profiles have been looked up at all. FALSE only while the
     * local query is still running — a device that legitimately holds no
     * profiles reports ready and falls back to the username.
     */
    ready: boolean;
}

/**
 * The name resolver and its readiness — see {@link useUserLabel} for the rule.
 *
 * Anything that WRITES a name as text (a form's author default, a picker's
 * options) must take this one and hold until `ready`. The fallback below cannot
 * tell "this account has no custom name" from "the profiles have not arrived
 * yet", and in the second case it answers with the Synology login name. Names
 * chosen in that window are stored as text and stay wrong: 46 Tagebücher were
 * filed under `P.Eichelbrönner` instead of `P. Eichelbrönner` that way.
 *
 * Read from the app's one user directory (see userDirectory).
 *
 * `ready` closes the loading window. It does NOT help a device whose profiles
 * are missing for good — that is the replication gap, which "Daten neu laden"
 * in the Profilmenü repairs (see db/replication.ts).
 */
export function useUserLabelState(): UserLabelState {
    const { label, profilesReady } = useUserDirectory();
    return useMemo(
        () => ({ label, ready: profilesReady }),
        [label, profilesReady]
    );
}

/**
 * Returns a resolver that yields a user's display name, preferring the
 * app-level custom name (from the synced `user_profiles`) and falling back to
 * the Synology username. The Synology `displayName` is deliberately skipped:
 * on bulk import it carries the account *description*, not a real name.
 *
 * To store a name rather than show one, take {@link useUserLabelState} and wait
 * for its `ready` — the fallback is fine on screen for a moment and permanent
 * in the database.
 */
export function useUserLabel(): (user: UserDocType) => string {
    return useUserLabelState().label;
}
